import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Compass, X } from 'lucide-react';
import { PageShell, Section } from '../../components/elevation';
import { useMethodIntroEnabled } from '../../features/method/hooks/useMethodIntroEnabled';
import { METHOD_PHASES, getPhaseBookMeaning, METHOD_TEASER_DISMISSED_KEY } from '../../lib/method/content';

type Step = 'intro' | 'decide' | 'engage' | 'gather' | 'arrange' | 'prioritize' | 'finish';

const STEP_ORDER: Step[] = ['intro', 'decide', 'engage', 'gather', 'arrange', 'prioritize', 'finish'];

function markSeen() {
  try {
    localStorage.setItem(METHOD_TEASER_DISMISSED_KEY, '1');
  } catch {
    // localStorage unavailable — non-fatal, tour just may show again next visit
  }
}

export default function MethodTour() {
  const navigate = useNavigate();
  const introEnabled = useMethodIntroEnabled();
  const [stepIndex, setStepIndex] = useState(0);
  const step = STEP_ORDER[stepIndex];

  if (!introEnabled) {
    return <Navigate to="/elevation/method" replace />;
  }

  const goNext = () => setStepIndex((i) => Math.min(i + 1, STEP_ORDER.length - 1));
  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));

  const skip = () => {
    markSeen();
    navigate('/prioritization/dashboard');
  };

  const finish = () => {
    markSeen();
    navigate('/prioritization/dashboard');
  };

  const phaseContent = step !== 'intro' && step !== 'finish'
    ? METHOD_PHASES.find((p) => p.phase === step)
    : undefined;

  return (
    <PageShell>
      <div className="max-w-xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-1.5">
          {STEP_ORDER.map((s, i) => (
            <span
              key={s}
              className={
                'h-1.5 w-6 rounded-full transition-colors ' +
                (i <= stepIndex ? '' : 'bg-charcoal-200 dark:bg-charcoal-700')
              }
              style={i <= stepIndex ? { backgroundColor: phaseContent?.color ?? '#3D4B6F' } : undefined}
            />
          ))}
        </div>
        <button
          onClick={skip}
          className="inline-flex items-center gap-1 text-xs font-medium text-charcoal-500 hover:text-charcoal-700 dark:text-charcoal-400 dark:hover:text-charcoal-200"
        >
          Skip tour <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {step === 'intro' && (
        <Section>
          <Compass className="h-8 w-8 text-primary-600 mb-4" />
          <h1 className="font-heading font-semibold text-2xl text-charcoal-900 dark:text-charcoal-50 mb-3">
            Before you dive in
          </h1>
          <p className="text-charcoal-700 dark:text-charcoal-300 leading-relaxed mb-4">
            Priorities.ai is built on <em>Managing Priorities</em>, the book by Harry Max. Its
            core framework is DEGAP — Decide, Engage, Gather, Arrange, Prioritize — and this app
            is organized around it, even where it doesn't say so out loud.
          </p>
          <p className="text-charcoal-700 dark:text-charcoal-300 leading-relaxed">
            Five short stops, one per phase. Each one shows what the book means by it and exactly
            where to go do it in this app.
          </p>
        </Section>
      )}

      {phaseContent && (
        <Section>
          <span
            className="font-mono text-xs uppercase tracking-wider"
            style={{ color: phaseContent.color }}
          >
            Phase {phaseContent.order} of 5
          </span>
          <h1
            className="font-heading font-semibold text-2xl mt-1 mb-4"
            style={{ color: phaseContent.color }}
          >
            {phaseContent.title}
          </h1>
          <p className="text-charcoal-800 dark:text-charcoal-100 leading-relaxed mb-4">
            {getPhaseBookMeaning(phaseContent.phase)}
          </p>
          <p className="text-charcoal-600 dark:text-charcoal-300 leading-relaxed mb-5">
            {phaseContent.inTheApp}
          </p>
          <Link
            to={phaseContent.primaryLink.path}
            onClick={markSeen}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: phaseContent.color }}
          >
            {phaseContent.primaryLink.label} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <p className="text-xs text-charcoal-400 dark:text-charcoal-500 mt-2">
            Opens the real thing — you'll leave the tour to go do it.
          </p>
        </Section>
      )}

      {step === 'finish' && (
        <Section>
          <h1 className="font-heading font-semibold text-2xl text-charcoal-900 dark:text-charcoal-50 mb-3">
            You've got the shape of it.
          </h1>
          <p className="text-charcoal-700 dark:text-charcoal-300 leading-relaxed mb-6">
            Decide, Engage, Gather, Arrange, Prioritize. Come back to{' '}
            <Link to="/elevation/method" className="text-primary-600 hover:underline">
              The Method
            </Link>{' '}
            any time from the nav bar — the phase pages, the six object types, and the technique
            toolbox are all still there.
          </p>
          <button
            onClick={finish}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white transition-colors"
          >
            Go to your dashboard <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </Section>
      )}

      <div className="flex items-center justify-between mt-8">
        <button
          onClick={goBack}
          disabled={stepIndex === 0}
          className="inline-flex items-center gap-1 text-sm text-charcoal-500 hover:text-charcoal-700 dark:text-charcoal-400 dark:hover:text-charcoal-200 disabled:opacity-0"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
        {step !== 'finish' && (
          <button
            onClick={goNext}
            className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            Next <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      </div>
    </PageShell>
  );
}
